import { Icon } from '@eventbrite/eds-icon';
import { ChevronUpChunky, MapPinChunky } from '@eventbrite/eds-iconography';
import { withOverlayControls } from '@eventbrite/eds-structure';
import { gettext } from '@eventbrite/i18n';
import {
    getLocationSuggestions,
    LocationAutocomplete,
} from '@eventbrite/location-autocomplete';
import { logEvent } from '@eventbrite/statsig';
import memoize from 'lodash/memoize';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Portal } from '../../../../components/portal/Portal';
import { HEAP_STICKY_LOCATION_PILL_CLICK } from '../../constants/analytics';
import {
    getUserCurrentLocation as getUserCurrentLocationAction,
    locationChange as locationChangeAction,
    searchLocationSuggestions as searchLocationSuggestionsAction,
} from '../../redux/location';
import './StickyLocationAutocompleteContainer.scss';

const getLocationSuggestionsMemoized = memoize(getLocationSuggestions);

const ChevronUp = (
    <Icon
        type={<ChevronUpChunky />}
        color="ui-blue-inverse--hover"
        size="small"
    />
);
const LocationPin = <Icon type={<MapPinChunky />} color="white" size="small" />;

const STICKY_LOCATION_AUTOCOMPLETE_PROPS = {
    prefix: ChevronUp,
    borderType: 'content',
    id: 'locationPicker',
    name: 'locationPicker',
    hideOutline: true,
    isLimitedSearch: true,
    preventInputValuePrefill: true,
    shouldRemoveConflictiveAria: true,
    suffix: LocationPin,
    'data-testid': 'locationPill',
};

interface StickyLocationAutoCompleteContainerProps {
    location: {
        isOnline?: boolean;
        currentPlace?: string;
        currentPlaceParent?: string;
        isUsingCurrentLocation?: boolean;
        locationDenied?: boolean;
        isWaitingForLocation?: boolean;
        latitude?: number;
        longitude?: number;
        placeId?: string;
    };
}

const _mapStateToProps = (state: StickyLocationAutoCompleteContainerProps) => ({
    currentPlace: state.location.isOnline
        ? gettext('Online events').toString()
        : state.location.currentPlace,
    placeholder: state.location.isOnline
        ? gettext('Online events')
        : state.location.currentPlace || gettext('Choose a location'),
    currentPlaceParent: state.location.currentPlaceParent,
    isUsingCurrentLocation: state.location.isUsingCurrentLocation,
    isUseCurrentLocationEnabled: !state.location.locationDenied,
    isOnline: state.location.isOnline,
    isWaitingForLocation: state.location.isWaitingForLocation,
    locationSuggestions: getLocationSuggestionsMemoized(
        state.location,
        !state.location.locationDenied,
    ),
    latitude: state.location.latitude,
    longitude: state.location.longitude,
    placeId: state.location.placeId,
});

const _mapDispatchToProps = {
    handleLocationSuggestionChange: searchLocationSuggestionsAction,
    handleLocationSelect: locationChangeAction,
    handleUseCurrentLocation: getUserCurrentLocationAction,
};

const StickyLocationComponent = ({ location, ...props }: any) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    useEffect(() => {
        if (isExpanded) {
            logEvent(HEAP_STICKY_LOCATION_PILL_CLICK);
        }
    }, [isExpanded]);

    /**
     * The idea behind using mutation observer is to capture the
     * expansion/click on Location pill. Ideally the LocationAutocomplete
     * opens the dropdown below the location text. However when using a sticky
     * location pill (which stays at the botton of viewport) we need dropdown
     * open above the component but directionally downwar. The way we are
     * acheiving it is to increase the height of the div and modify the
     * structure of text to sync with the dropdown design
     */
    useEffect(() => {
        if (containerRef.current) {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (
                        mutation.type === 'attributes' &&
                        mutation.attributeName === 'aria-expanded'
                    ) {
                        const target = mutation.target as HTMLElement;
                        if (
                            !isExpanded &&
                            target.getAttribute('aria-expanded') === 'true'
                        ) {
                            if (containerRef.current) {
                                setIsExpanded(true);
                            }
                        } else if (
                            isExpanded &&
                            target.getAttribute('aria-expanded') !== 'true'
                        ) {
                            if (containerRef.current) {
                                setIsExpanded(false);
                            }
                        }
                    }
                });
            });

            const config = {
                attributes: true,
                attributeFilter: ['aria-expanded'],
                subtree: true,
            };
            observer.observe(containerRef.current, config);

            return () => {
                observer.disconnect();
            };
        }
    }, [isExpanded]);

    const classNames = `sticky__browse__location_div ${
        isExpanded ? 'expanded' : 'default'
    }`;
    return (
        <Portal>
            <div
                data-testid="stickyLocation"
                className={classNames}
                ref={containerRef}
                role="combobox"
                aria-expanded={isExpanded}
            >
                <LocationAutocomplete
                    autocompleteProps={STICKY_LOCATION_AUTOCOMPLETE_PROPS}
                    shouldDispatchSearchActions={true}
                    location={location}
                    {...props}
                />
            </div>
        </Portal>
    );
};

export default withOverlayControls<any>(
    connect(
        _mapStateToProps,
        _mapDispatchToProps,
    )(React.memo(StickyLocationComponent)),
);
